import { useState, useEffect } from 'react';
import ApiService from '../services/ApiService';
import './SurveyPopup.css';

/**
 * SurveyPopup Component
 * Help Us Improve survey popup matching mockUI.html
 */
const SurveyPopup = ({ activeEventId }) => {
  const [isOpen, setIsOpen] = useState(true); // Show immediately
  const [selectedSegment, setSelectedSegment] = useState('');
  const [availableSegments, setAvailableSegments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [sessionId, setSessionId] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  console.log('SurveyPopup rendered, isOpen:', isOpen, 'availableSegments:', availableSegments);

  // Generate or retrieve session ID from localStorage
  useEffect(() => {
    let sid = localStorage.getItem('survey_session_id');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('survey_session_id', sid);
    }
    setSessionId(sid);
  }, []);

  // Fetch available segments with surveys
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        console.log('Fetching survey segments...');
        const segments = await ApiService.getSurveySegments();
        console.log('Survey segments fetched:', segments);
        setAvailableSegments(segments);
      } catch (err) {
        console.error('Failed to fetch survey segments:', err);
      }
    };

    fetchSegments();
  }, []);

  // Fetch questions when segment selected
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedSegment || !sessionId) return;

      setLoading(true);
      setError(null);

      try {
        // Check if already submitted for this segment + active event
        const submitted = await ApiService.checkSurveySubmission(selectedSegment, sessionId, activeEventId);
        setHasSubmitted(submitted);

        // Get questions
        const qs = await ApiService.getSurveyQuestions(selectedSegment);
        setQuestions(qs);

        // Initialize answers object
        const initialAnswers = {};
        qs.forEach(q => {
          if (q.questionType === 'checkbox') {
            initialAnswers[q.id] = [];
          } else {
            initialAnswers[q.id] = '';
          }
        });
        setAnswers(initialAnswers);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [selectedSegment, sessionId, activeEventId]);

  const handleSegmentChange = (e) => {
    setSelectedSegment(e.target.value);
    setSuccessMessage('');
    setError(null);
  };

  const handleCheckboxChange = (questionId, option) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (current.includes(option)) {
        return { ...prev, [questionId]: current.filter(o => o !== option) };
      } else {
        return { ...prev, [questionId]: [...current, option] };
      }
    });
  };

  const handleTextChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      // Format responses
      const responses = questions.map(q => ({
        questionId: q.id,
        answer: answers[q.id],
      }));

      await ApiService.submitSurvey(selectedSegment, sessionId, responses, activeEventId);
      
      setSuccessMessage('Thank you for your feedback!');
      setHasSubmitted(true);

      // Close popup after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to submit survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('survey_popup_dismissed', 'true');
  };

  if (!isOpen) {
    console.log('Survey popup hidden because isOpen is false');
    return null;
  }

  return (
    <div className="survey-popup" id="improve-popup">
      <div className="popup-header">
        <span>Help Us Improve</span>
        <span className="close-x" onClick={handleClose}>×</span>
      </div>

      <div className="popup-body">
        {/* Segment Selection */}
        <select 
          id="segmentSelect" 
          value={selectedSegment}
          onChange={handleSegmentChange}
          disabled={loading}
        >
          <option value="">Select Your Segment</option>
          {availableSegments.map(segment => (
            <option key={segment.code} value={segment.code}>
              {segment.displayName}
            </option>
          ))}
        </select>

        {/* Loading State */}
        {loading && selectedSegment && (
          <div className="loading-message">Loading questions...</div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">{error}</div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        {/* Already Submitted Message */}
        {hasSubmitted && !successMessage && (
          <div className="info-message">
            You have already submitted feedback for this segment. Thank you!
          </div>
        )}

        {/* Questions */}
        {selectedSegment && questions.length > 0 && !hasSubmitted && !successMessage && (
          <div className="question-content">
            {questions.map(question => (
              <div key={question.id} className="question-block">
                <span className="question-label">{question.questionText}</span>

                {question.questionType === 'checkbox' && (
                  <div className="checkbox-options">
                    {question.options.map(option => (
                      <label key={option} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={answers[question.id]?.includes(option) || false}
                          onChange={() => handleCheckboxChange(question.id, option)}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                )}

                {question.questionType === 'text' && (
                  <textarea
                    value={answers[question.id] || ''}
                    onChange={(e) => handleTextChange(question.id, e.target.value)}
                    placeholder="Your answer..."
                  />
                )}

                {question.questionType === 'radio' && (
                  <div className="radio-options">
                    {question.options.map(option => (
                      <label key={option} className="radio-item">
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={option}
                          checked={answers[question.id] === option}
                          onChange={(e) => handleTextChange(question.id, e.target.value)}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* No Survey Message */}
        {selectedSegment && questions.length === 0 && !loading && (
          <div style={{ color: '#999', textAlign: 'center', padding: '10px', fontSize: '14px' }}>
            No items found
          </div>
        )}
      </div>

      {/* Footer with buttons */}
      {selectedSegment && questions.length > 0 && !hasSubmitted && !successMessage && (
        <div className="popup-footer">
          <button 
            className="footer-btn btn-close-outline" 
            onClick={handleClose}
            disabled={loading}
          >
            Close
          </button>
          <button 
            className="footer-btn btn-save" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SurveyPopup;
